import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, ArrowRight, Check } from "lucide-react";

export const PENDING_ESTIMATE_KEY = "pendingCostEstimate";

// Each choice maps to the eBook cost-estimator keys so the pick carries into the app
const CHOICES = [
  {
    key: "registeredAgent",
    label: "Registered agent",
    options: [
      { id: "self", label: "I'll be my own", cost: 0, hint: "Free — you need a Texas street address" },
      { id: "service", label: "Use a service", cost: 75, hint: "$75/yr keeps your home address private" },
    ],
  },
  {
    key: "operatingAgreement",
    label: "Operating agreement",
    options: [
      { id: "template", label: "Use a template", cost: 0, hint: "Free — included in the guide" },
      { id: "drafted", label: "Have it drafted", cost: 50, hint: "$50 custom draft, 3 business days" },
    ],
  },
  {
    key: "licensesPermits",
    label: "Licenses & permits",
    options: [
      { id: "none", label: "None needed", cost: 0, hint: "Most online & service businesses" },
      { id: "some", label: "Probably some", cost: 150, hint: "Typical $50–$300 (sales tax permit is free)" },
    ],
  },
];

const STATE_FEE = 300;

export default function HeroCostCalculator() {
  const navigate = useNavigate();
  const [picks, setPicks] = useState({ registeredAgent: "self", operatingAgreement: "template", licensesPermits: "none" });

  const { total, estimate } = useMemo(() => {
    const est = { stateFilingFee: STATE_FEE, ein: 0, bankAccount: 0 };
    let sum = STATE_FEE;
    for (const c of CHOICES) {
      const opt = c.options.find((o) => o.id === picks[c.key]);
      est[c.key] = opt.cost;
      sum += opt.cost;
    }
    return { total: sum, estimate: est };
  }, [picks]);

  const start = () => {
    localStorage.setItem(PENDING_ESTIMATE_KEY, JSON.stringify(estimate));
    navigate("/login?mode=register");
  };

  return (
    <div data-testid="hero-cost-calculator" className="relative mx-auto mt-10 w-full max-w-3xl text-left">
      <div className="absolute -bottom-2 -right-2 h-full w-full rounded-3xl bg-[#1E3A8A]" aria-hidden="true" />
      <div className="relative rounded-3xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-5 shadow-[6px_6px_0px_var(--c-shadow)] sm:p-7">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="inline-flex items-center gap-2 font-heading text-base font-black text-[var(--c-text)] sm:text-lg">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg border-2 border-[var(--c-border)] bg-[#1E3A8A] text-white"><Calculator className="h-4 w-4" /></span>
            What will my Texas LLC cost?
          </p>
          <p className="text-xs font-semibold text-[var(--c-muted)]">State filing fee: <span className="text-[var(--c-text)]">${STATE_FEE}</span> &middot; EIN: <span className="text-[var(--c-text)]">Free</span></p>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          {CHOICES.map((c) => (
            <fieldset key={c.key} data-testid={`calc-group-${c.key}`} className="rounded-2xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] p-3">
              <legend className="px-1 text-[11px] font-bold uppercase tracking-wide text-[var(--c-muted)]">{c.label}</legend>
              <div className="space-y-1.5">
                {c.options.map((o) => {
                  const active = picks[c.key] === o.id;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      data-testid={`calc-option-${c.key}-${o.id}`}
                      aria-pressed={active}
                      onClick={() => setPicks((p) => ({ ...p, [c.key]: o.id }))}
                      className={`flex w-full items-start gap-2 rounded-xl border-2 px-3 py-2 text-left transition-colors ${
                        active ? "border-[#1E3A8A] bg-[#1E3A8A]/10" : "border-[var(--c-border-light)] hover:border-[var(--c-border)]"
                      }`}
                    >
                      <span className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 ${active ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-[var(--c-border)]"}`}>
                        {active && <Check className="h-2.5 w-2.5" />}
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center justify-between gap-2 text-sm font-semibold text-[var(--c-text)]">
                          {o.label}<span className="text-xs font-bold text-[#1E3A8A]">{o.cost ? `+$${o.cost}` : "$0"}</span>
                        </span>
                        <span className="block text-[11px] leading-snug text-[var(--c-muted)]">{o.hint}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>

        <div className="mt-5 flex flex-col items-start gap-4 border-t-2 border-[var(--c-border)] pt-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[var(--c-muted)]">Your estimated startup cost</p>
            <p className="font-heading text-4xl font-black text-[#1E3A8A]" data-testid="calc-total">${total}</p>
            <p className="text-xs text-[var(--c-muted)]">vs. $199–$299 in service fees <em>plus</em> the $300 state fee with a filing company.</p>
          </div>
          <button
            data-testid="calc-start-button"
            onClick={start}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border-2 border-[var(--c-border)] bg-[#C8102E] px-6 py-3 text-sm font-bold text-white shadow-[4px_4px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5"
          >
            Start free &amp; save this estimate <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
